import React, { useMemo } from "react";
import type { DataSource, SourceEntity } from "../../../../types/dataVault.types";
import {
  SearchableSelect,
  type SearchableSelectOption
} from "../../../../components/SearchableSelect/SearchableSelect";

const selectorInputClass =
  "tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 focus:tw-border-[#0f172a] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#0f172a]/10 disabled:tw-opacity-60";

// numeric so item_2 sorts before item_10, base sensitivity so case never splits the list
const optionCollator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

/** Schema first (unschema'd last), then name — matching how the option reads. */
const bySchemaThenName = (left: SourceEntity, right: SourceEntity) => {
  const leftSchema = (left.source_schema ?? "").trim();
  const rightSchema = (right.source_schema ?? "").trim();
  if (Boolean(leftSchema) !== Boolean(rightSchema)) {
    return leftSchema ? -1 : 1;
  }
  const bySchema = optionCollator.compare(leftSchema, rightSchema);
  return bySchema !== 0
    ? bySchema
    : optionCollator.compare(left.source_entity_name ?? "", right.source_entity_name ?? "");
};

type SelectorProps = {
  dataSources: DataSource[];
  loading: boolean;
  error: string | null;
  selectedDataSource: string;
  selectedSourceEntity: string;
  onDataSourceChange: (value: string, dataSource: DataSource | null) => void;
  onSourceEntityChange: (value: string, sourceEntity: SourceEntity | null) => void;
  disabled?: boolean;
};

export function DataSourceEntitySelectors({
  dataSources,
  loading,
  error,
  selectedDataSource,
  selectedSourceEntity,
  onDataSourceChange,
  onSourceEntityChange,
  disabled = false
}: SelectorProps): JSX.Element {
  const selectedDataSourceRecord = useMemo(
    () =>
      dataSources.find((dataSource) => dataSource.data_source_name === selectedDataSource) ?? null,
    [dataSources, selectedDataSource]
  );

  const sortedDataSources = useMemo(
    () =>
      [...dataSources].sort((left, right) =>
        optionCollator.compare(left.data_source_name ?? "", right.data_source_name ?? "")
      ),
    [dataSources]
  );

  const sourceEntities = useMemo(
    () => [...(selectedDataSourceRecord?.source_entity ?? [])].sort(bySchemaThenName),
    [selectedDataSourceRecord]
  );

  const dataSourceOptions = useMemo<SearchableSelectOption[]>(
    () =>
      sortedDataSources.map((source) => ({
        label: source.data_source_name,
        value: source.data_source_name
      })),
    [sortedDataSources]
  );

  const sourceEntityOptions = useMemo<SearchableSelectOption[]>(
    () =>
      sourceEntities.map((entity) => ({
        label: entity.source_schema
          ? `${entity.source_schema}.${entity.source_entity_name}`
          : entity.source_entity_name,
        value: entity.source_entity_name
      })),
    [sourceEntities]
  );

  return (
    <>
      <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
        <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
          Data source
        </label>
        <SearchableSelect
          value={selectedDataSource}
          options={dataSourceOptions}
          onChange={(value) => {
            const record = dataSources.find((item) => item.data_source_name === value) ?? null;
            onDataSourceChange(value, record);
          }}
          disabled={disabled || loading || dataSources.length === 0}
          placeholder={
            loading
              ? "Loading data sources..."
              : dataSources.length
              ? "Select data source"
              : "No data sources available"
          }
          aria-label="Data source"
          inputClassName={selectorInputClass}
        />
      </div>

      <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
        <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
          Source entity
        </label>
        <SearchableSelect
          value={selectedSourceEntity}
          options={sourceEntityOptions}
          onChange={(value) => {
            const record = sourceEntities.find((item) => item.source_entity_name === value) ?? null;
            onSourceEntityChange(value, record);
          }}
          disabled={disabled || !selectedDataSource || sourceEntities.length === 0}
          placeholder={
            !selectedDataSource
              ? "Select data source first"
              : sourceEntities.length
              ? "Select source entity"
              : "No source entities available"
          }
          aria-label="Source entity"
          inputClassName={selectorInputClass}
        />
      </div>

      {error ? <p className="tw-text-xs tw-font-semibold tw-text-rose-600">{error}</p> : null}
    </>
  );
}
